/**
 * Quantro Flow — Single source of truth for all user-facing copy.
 *
 * Conventions:
 *   - Hierarchical, dot-addressable keys resolved via t('a.b.c')
 *   - Variable interpolation: "Hello {{name}}" → t('key', { name: 'Ana' })
 *   - Never hard-code user-facing strings in components. Add them here first.
 *   - If a key is missing in the active language, EN is used as fallback;
 *     if still missing, the key itself is returned (never breaks UI).
 *
 * Adding a new language:
 *   1. Duplicate the `en` tree at the bottom.
 *   2. Translate leaf values only — preserve the shape.
 *   3. Update SUPPORTED_LANGUAGES in LanguageContext.
 */

export const translations = {
  // ────────────────────────────────────────────────────────────
  // Español
  // ────────────────────────────────────────────────────────────
  es: {
    // Onboarding ligero (3 preguntas tras signup)
    onboarding_lite: {
      title: 'Configura tu cuenta',
      title_with_name: 'Hola {{name}}, configura tu cuenta',
      subtitle: 'Tres datos rápidos para personalizar Quantro Flow a tu operación.',
      country_label: 'País donde opera tu empresa',
      country_hint: 'Usamos esto para moneda, zona horaria y normativas locales.',
      country_placeholder: 'Selecciona un país',
      industry_label: 'Giro de tu empresa',
      industry_hint: 'Esto personaliza las recomendaciones del agente IA.',
      industry_placeholder: 'Selecciona un giro',
      industries: {
        real_estate: 'Bienes raíces',
        healthcare: 'Salud / Clínicas',
        consulting: 'Consultoría',
        ecommerce: 'E-commerce',
        technology: 'Tecnología / Software',
        finance: 'Finanzas / Seguros',
        education: 'Educación',
        manufacturing: 'Manufactura',
        retail: 'Comercio minorista',
        hospitality: 'Hospitalidad / Restaurantes',
        legal: 'Legal',
        marketing: 'Marketing / Agencia',
        construction: 'Construcción',
        agriculture: 'Agricultura',
        automotive: 'Automotriz',
        logistics: 'Logística',
        nonprofit: 'ONG / Sin fines de lucro',
        other: 'Otro',
      },
      company_label: 'Nombre de la empresa',
      company_hint: 'Aparecerá en tu workspace y en los correos que envíe el agente.',
      company_placeholder: 'Ej. Acme Consulting',
      continue_to_app: 'Continuar a la plataforma',
      saving: 'Guardando…',
      saved_title: 'Configuración guardada',
      saved_desc: 'Personalizamos Quantro Flow a tu operación.',
      save_failed: 'No se pudo guardar. Intenta de nuevo.',
      fill_all_fields: 'Completa los tres campos para continuar.',
    },
    // Plan y Uso (new section)
    plan_usage: {
      nav_label: 'Plan y Uso',
      page_title: 'Plan y Uso',
      page_subtitle: 'Tu plan, consumo y cuenta en un solo lugar',
      // Plan card
      current_plan: 'Plan actual',
      plan_name: 'Plan',
      status_active: 'Activo',
      status_trial: 'Trial',
      status_expired: 'Vencido',
      renews_on: 'Se renueva el',
      next_billing: 'Próxima facturación',
      view_plan: 'Ver mi plan',
      upgrade_plan: 'Actualizar plan',
      // Usage card
      api_usage: 'Uso de API',
      api_calls_this_month: 'Llamadas este mes',
      monthly_limit: 'Límite mensual',
      usage_percent: '{{percent}}% utilizado',
      overage: 'Extra',
      breakdown_title: 'Desglose por módulo',
      inbox_ai: 'Inbox AI',
      crm: 'CRM',
      content_engine: 'Motor de Contenido',
      automations: 'Automatizaciones',
      calls: 'llamadas',
      share: '{{pct}}% del total',
      // Billing card
      billing: 'Facturación',
      payment_method: 'Método de pago',
      no_payment_method: 'Sin método de pago',
      manage_payments: 'Gestionar pagos',
      amount_due: 'Monto a pagar',
      // Account card
      account: 'Cuenta',
      user: 'Usuario',
      workspace: 'Espacio de trabajo',
      switch_account: 'Cambiar cuenta',
      signout: 'Cerrar sesión',
      view_account: 'Ver cuenta',
      // Supabase-backed signals
      source_supabase: 'Datos en vivo desde Supabase · ai_usage',
      plan_unavailable: 'Plan no disponible',
      refresh: 'Actualizar',
      no_usage_yet: 'Aún no hay consumo este mes.',
      limit_blocked: 'Necesitas un plan activo para usar las funciones inteligentes de Quantro Flow.',
      limit_coupon: 'Tu plan tiene un cupón aplicado · 500 llamadas mensuales.',
      limit_test_user: 'Cuenta de testing interna · acceso máximo.',
      // Credits IA (USD-based)
      ai_credits: 'Créditos IA',
      ai_credits_subtitle: 'Saldo en USD que se descuenta por costo real de cada request.',
      credits_unit: 'USD',
      credits_used: 'Usados: {{used}}',
      credits_consumed: 'Consumido',
      credits_source_quantro: 'API Quantro · gpt-4o-mini',
      credits_source_user: 'API propia',
      credits_source_blocked: 'Sin créditos',
      credits_low_warning: 'Te quedan menos del 20% de tus créditos IA. Considera actualizar tu plan o agregar tu propia API key.',
      credits_fallback_user_key: 'Estás usando tu propia API key de OpenAI. Quantro no descuenta créditos.',
      credits_blocked_message: 'Se acabaron tus créditos IA. Agrega tu propia API key de OpenAI o actualiza tu plan para seguir usando funciones inteligentes.',
      add_own_api_key: 'Agregar mi API key',
      credits_pricing_hint: 'Quantro fuerza gpt-4o-mini al usar tus créditos · entrada $0.15/1M · salida $0.60/1M tokens.',
    },
    billing: {
      select_plan_title: 'Elige tu plan',
      select_plan_subtitle: 'Gestiona tu suscripción, método de pago y facturación.',
      select_plan_cta: 'Elegir plan',
      current_plan_cta: 'Plan actual',
      current_plan_badge: 'Actual',
      most_popular: 'Más popular',
      period_monthly: 'Mensual',
      period_annual: 'Anual',
      annual_save: 'Ahorra 20%',
      annual_perk: '· 2 meses gratis',
      annual_perk_microcopy: 'Equivale a 2 meses gratis al pagar anual.',
      per_month_short: 'mes',
      billed_annually: 'Facturado anual · ${{total}} USD',
      upgrade_plan: 'Actualizar plan',
      manage_subscription: 'Gestionar suscripción',
      change_plan: 'Cambiar plan',
      redirecting: 'Redirigiendo…',
      checkout_failed: 'No se pudo iniciar el pago. Intenta de nuevo.',
      portal_failed: 'No se pudo abrir el portal de cliente.',
      checkout_success_title: '¡Pago confirmado!',
      checkout_success_desc: 'Estamos activando tu plan. Puede tardar unos segundos.',
      checkout_cancelled_title: 'Pago cancelado',
      checkout_cancelled_desc: 'No realizamos ningún cargo.',
      microcopy: 'Gestiona tu suscripción, método de pago y facturación desde un solo lugar.',
      secured_by_stripe: 'Pagos procesados de forma segura por Stripe',
      error_hint: 'Abre la consola del navegador (F12 → Console) para ver el detalle completo. También revisa los logs de la Edge Function en Supabase Dashboard → Edge Functions → create-checkout-session → Logs.',
    },
    // Automation Policies (new interactive copy)
    automation_policies: {
      page_title: 'Políticas de Automatización',
      page_subtitle: 'Define cómo la IA actúa sobre cada intent y cuándo escalar a humanos',
      tab_policies: 'Intent Policies',
      tab_rules: 'Escalation Rules',
      new_policy: 'Nueva política',
      new_rule: 'Nueva regla',
      edit: 'Editar',
      delete: 'Eliminar',
      save: 'Guardar',
      cancel: 'Cancelar',
      confirm_delete_title: '¿Eliminar este elemento?',
      confirm_delete_desc: 'Esta acción no se puede deshacer.',
      active: 'Activo',
      inactive: 'Inactivo',
      empty_policies_title: 'Aún no hay políticas',
      empty_policies_desc: 'Crea tu primera política para automatizar acciones según el intent detectado.',
      empty_rules_title: 'Aún no hay reglas de escalación',
      empty_rules_desc: 'Define cuándo la IA debe pasar el control a un humano.',
      // Policy form
      policy_name: 'Nombre',
      policy_intent: 'Tipo de intent',
      policy_mode: 'Modo',
      policy_confidence: 'Umbral de confianza',
      policy_daily_limit: 'Límite diario',
      policy_status: 'Estado',
      mode_auto_execute: 'Auto-ejecutar',
      mode_require_approval: 'Requiere aprobación',
      mode_suggest_only: 'Solo sugerir',
      // Intent options
      intent_new_lead: 'Nuevo lead',
      intent_inbound_msg: 'Mensaje entrante',
      intent_follow_up: 'Follow-up',
      intent_schedule: 'Agendamiento',
      intent_crm_update: 'Actualización CRM',
      intent_content_gen: 'Generación de contenido',
      intent_onboarding: 'Onboarding task',
      intent_human_escalation: 'Escalación humana',
      // Rule form
      rule_name: 'Nombre',
      rule_condition: 'Condición',
      rule_action: 'Acción',
      rule_threshold: 'Umbral (opcional)',
      rule_status: 'Estado',
      cond_low_confidence: 'Baja confianza',
      cond_vip_user: 'Usuario VIP',
      cond_complaint: 'Queja detectada',
      cond_missing_data: 'Datos faltantes',
      cond_integration_fail: 'Fallo de integración',
      action_send_human: 'Enviar a humano',
      action_confirm: 'Solicitar confirmación',
      action_retry: 'Reintentar',
      action_hold_review: 'Retener para revisión',
      saved_toast: 'Guardado correctamente',
      deleted_toast: 'Eliminado',
      save_failed: 'No se pudo guardar',
    },

    auth: {
      sign_in_title: 'Bienvenido a Quantro Flow',
      sign_in_subtitle: 'Tu Sistema Operativo de Negocios. Inicia sesión para continuar.',
      sign_in_with_google: 'Continuar con Google',
      signing_in: 'Iniciando sesión…',
      signing_out: 'Cerrando sesión…',
      signed_out_title: 'Has cerrado sesión',
      signed_out_subtitle: 'Vuelve cuando quieras.',
      login_failed_title: 'No se pudo iniciar sesión',
      login_failed_desc: 'Intenta nuevamente o contacta a soporte.',
      welcome_back: 'Bienvenido de vuelta, {{name}}',
      logout: 'Cerrar sesión',
      my_workspace: 'Mi Espacio de Trabajo',
      switch_workspace: 'Cambiar de espacio',
      active_workspace: 'Espacio activo',
      you_are_owner: 'Propietario',
      secure_session: 'Sesión segura',
      secured_by: 'Protegido por Supabase Auth',
      no_workspace_title: 'Aún no tienes un espacio de trabajo',
      no_workspace_subtitle: 'Crea uno para empezar a operar.',
      new_workspace: 'Nuevo espacio',
      email_label: 'Correo electrónico',
      email_placeholder: 'tu@empresa.com',
      password_label: 'Contraseña',
      password_placeholder: 'Tu contraseña',
      full_name_label: 'Nombre completo',
      full_name_placeholder: 'Nombre y apellido',
      full_name_invalid: 'Ingresa tu nombre y apellido',
      sign_in_cta: 'Iniciar sesión',
      sign_up_cta: 'Crear cuenta',
      toggle_to_signup: '¿No tienes cuenta? Crear una',
      toggle_to_signin: '¿Ya tienes cuenta? Iniciar sesión',
      forgot_password: '¿Olvidaste tu contraseña?',
      signup_success_title: 'Revisa tu correo',
      signup_success_desc: 'Te enviamos un enlace de confirmación para activar tu cuenta.',
      password_min: 'La contraseña debe tener al menos 8 caracteres.',
      missing_fields: 'Ingresa correo y contraseña.',
      account_confirmed: 'Cuenta confirmada',
    },
    members: {
      title: 'Miembros y Permisos',
      subtitle: 'Administra quién accede a este espacio y qué puede hacer.',
      tab_members: 'Miembros',
      tab_invites: 'Invitaciones',
      your_role: 'Tu rol',
      empty_invites: 'No hay invitaciones activas. Crea un enlace para invitar a tu equipo.',
      invite_link_created: 'Enlace de invitación creado',
      invite_link_copied: 'Enlace copiado al portapapeles',
      copy_link: 'Copiar enlace',
      revoke: 'Revocar',
      revoked: 'Revocado',
      revoke_confirm: '¿Seguro que quieres revocar esta invitación? El enlace dejará de funcionar.',
      remove_member: 'Quitar miembro',
      remove_confirm: '¿Quitar a este miembro del espacio?',
      leave_workspace: 'Salir del espacio',
      leave_confirm: '¿Quieres salir de este espacio? Perderás acceso a sus datos.',
      change_role: 'Cambiar rol',
      generate_invite: 'Generar enlace',
      generating: 'Generando...',
      invite_role_label: 'Rol que recibirá el invitado',
      invite_max_uses: 'Máximo de usos',
      invite_expires: 'Expira en (días)',
      invite_email_optional: 'Email (opcional)',
      invite_full_name_optional: 'Nombre completo (opcional)',
      invite_create_title: 'Nueva invitación',
      member_count: '{{count}} miembros',
      pending_count: '{{count}} pendientes',
      transfer_ownership: 'Transferir propiedad',
      transfer_warning: 'Al transferir la propiedad serás degradado a Admin. ¿Continuar?',
      role_owner: 'Propietario',
      role_admin: 'Admin',
      role_manager: 'Gerente',
      role_operator: 'Operador',
      role_agent: 'Agente',
      role_owner_desc: 'Control total. Único por espacio. Puede transferir propiedad.',
      role_admin_desc: 'Gestiona miembros, integraciones, perfil y simulación.',
      role_manager_desc: 'Edita políticas de automatización, reglas y plantillas.',
      role_operator_desc: 'Uso diario: bandeja, agenda, CRM, generación de contenido.',
      role_agent_desc: 'Solo lectura en la mayoría de módulos.',
      // Quantro role taxonomy (matches Supabase org_members)
      role_leader: 'Líder',
      role_accountant: 'Contador',
      role_member: 'Miembro',
      role_viewer: 'Visualizador',
      role_leader_desc: 'Gestiona miembros, integraciones, perfil y simulación.',
      role_accountant_desc: 'Edita políticas de automatización, reglas y plantillas.',
      role_member_desc: 'Uso diario: bandeja, agenda, CRM, generación de contenido.',
      role_viewer_desc: 'Solo lectura en la mayoría de módulos.',
      forbidden_title: 'Acción no permitida',
      forbidden_desc: 'Tu rol actual no tiene permisos para esta acción.',
      not_owner_promotion: 'Solo el Propietario puede asignar el rol de Admin u Owner.',
    },
    audit: {
      tab_label: 'Auditoría',
      empty: 'No hay eventos registrados aún.',
      action_invitation_created: 'Invitación creada',
      action_invitation_accepted: 'Invitación aceptada',
      action_access_revoked: 'Acceso revocado',
      action_user_deleted: 'Usuario eliminado',
      action_role_changed: 'Rol cambiado',
      action_companies_assigned: 'Empresa asignada',
      action_companies_removed: 'Empresa removida',
      action_permissions_modified: 'Permisos modificados',
      action_onboarding_completed: 'Onboarding completado',
      action_unknown: 'Evento del sistema',
      time_just_now: 'hace un momento',
      time_minutes_ago: 'hace {{count}} min',
      time_hours_ago: 'hace {{count}} h',
      time_days_ago: 'hace {{count}} d',
      filter_all: 'Todos',
      filter_member: 'Miembro',
      load_more: 'Cargar más',
      header_actor: 'Actor',
      header_target: 'Objetivo',
      action_added: 'Miembro agregado',
      filter_start_date: 'Desde',
      filter_end_date: 'Hasta',
      filter_action: 'Acción',
      filter_action_all: 'Todas las acciones',
      export_button: 'Exportar',
      export_menu_label: 'Formato de exportación',
      export_csv: 'Descargar CSV',
      export_json: 'Descargar JSON',
      export_success: 'Auditoría exportada en {{format}}',
      export_error: 'No se pudo exportar la auditoría',
    },
    workspace: {
      rename_button: 'Renombrar espacio de trabajo',
      rename_title: 'Renombrar espacio de trabajo',
      rename_description: 'Personaliza el nombre visible para todos los miembros. Puedes cambiarlo las veces que quieras.',
      rename_label: 'Nombre del espacio',
      rename_placeholder: 'Ej: Mi Empresa',
      rename_hint: 'Entre 1 y 80 caracteres. Los cambios se registran en el log de auditoría.',
      rename_save: 'Guardar',
      rename_success: 'Espacio renombrado a "{{name}}"',
      rename_error: 'No se pudo renombrar el espacio',
      rename_empty: 'El nombre no puede estar vacío',
    },
    welcome: {
      sign_out: 'Salir',
      progress_aria: 'Progreso de activación',
      preview: {
        badge_preview: 'Vista previa',
        badge_demo: 'Modo demo',
        badge_real: 'Datos reales',
        badge_connecting: 'Conectando',
        connecting: 'Conectando...',
        continue_demo: 'Continuar con demo',
        real_pending_title: 'Conexión real próximamente',
        real_pending_desc: 'Pronto vas a poder conectar tu cuenta de Google. Por ahora seguimos en modo demo.',
        real_pending_desc_crm: 'La integración real con CRM llega en breve. Continuamos en modo demo.',
        real_pending_desc_webhooks: 'Pronto vas a poder configurar webhooks reales. Continuamos en modo demo.',
        real_failed_title: 'No pudimos conectar con Google',
        real_connected_title: '¡Google conectado!',
        real_connected_title_v2: '¡{{provider}} conectado!',
        real_synced_desc: 'Sincronizamos {{emails}} correos y {{events}} eventos de {{account}}.',
        real_connected_no_sync_title: 'Google conectado, sincronización pendiente',
        real_connected_no_sync_title_v2: '{{provider}} conectado, sincronización pendiente',
        real_connected_no_sync_desc: 'La cuenta quedó vinculada pero la sincronización inicial falló. Vamos a reintentarlo en segundo plano.',
      },
      connect_modal: {
        title_inbox: 'Elige tu proveedor de correo',
        title_calendar: 'Elige tu proveedor de calendario',
        desc_inbox: 'Conecta Gmail u Outlook. Sincronizamos tus correos y los priorizamos automáticamente.',
        desc_calendar: 'Conecta Google Calendar u Outlook Calendar. Detectamos disponibilidad y automatizamos seguimientos.',
        google_title: 'Google',
        google_subtitle: 'Gmail · Google Calendar',
        microsoft_title: 'Microsoft',
        microsoft_subtitle: 'Outlook · Calendar',
        google_failed_title: 'No pudimos conectar con Google',
        microsoft_failed_title: 'No pudimos conectar con Microsoft',
        google_unavailable_desc: 'La integración con Google aún no está configurada en tu workspace. Avísale a tu administrador.',
        microsoft_unavailable_desc: 'La integración con Microsoft aún no está configurada en tu workspace. Avísale a tu administrador.',
        coming_soon_badge: 'Próximamente',
        permission_missing_title: 'Falta un permiso para completar la conexión',
        permission_missing_desc: 'Autorizaste {{provider}} pero falta: {{scopes}}. Sin esto no podemos sincronizar datos reales.',
        reauthorize_cta: 'Autorizar nuevamente',
        footnote: 'Solo accedemos a lo necesario y nunca enviamos correos sin tu permiso. Puedes desconectar cuando quieras.',
      },
      start: {
        eyebrow: 'Tu cuenta ya está lista',
        eyebrow_with_name: 'Bienvenido, {{name}}',
        title: '¿Cómo quieres empezar?',
        subtitle: 'Elige por dónde activamos Quantro contigo. Puedes cambiarlo después.',
        recommended_badge: 'Recomendado',
        continue_cta: 'Empezar',
        choice_email_title: 'Conectar correo',
        choice_email_desc: 'Lo más rápido. Quantro empieza a organizar tus conversaciones desde el primer minuto.',
        choice_tools_title: 'Conectar herramientas',
        choice_tools_desc: 'Calendario, CRM y webhooks. Construimos tu operación pieza por pieza.',
        choice_explore_title: 'Explorar primero',
        choice_explore_desc: 'Verás Quantro en acción con datos de demostración. Conectas cuando quieras.',
      },
      checkout: {
        eyebrow: 'Activado',
        title: 'Quantro está listo para operar',
        subtitle: 'Tu plan Starter ya está activo. Conectemos tu operación.',
        plan_starter_name: 'Plan Starter',
        plan_active_badge: 'Activo',
        feat_inbox: 'Bandeja Inteligente con triage por IA',
        feat_calendar: 'Agenda y disponibilidad sincronizadas',
        feat_crm: 'CRM con seguimiento automatizado',
        feat_automations: 'Automatizaciones y webhooks',
        continue_cta: 'Continuar',
        upgrade_link: 'Ver planes superiores más adelante',
      },
      inbox: {
        title: 'Conecta tu correo',
        subtitle: 'Aquí es donde todo comienza.\n\nQuantro organiza tus conversaciones, detecta oportunidades\ny sugiere acciones automáticamente.',
        connect_cta: 'Conectar con Google',
        skip_cta: 'Continuar con demo',
        checking_connection: 'Verificando tu conexión con Google...',
        preview_microcopy: 'Así se verá tu bandeja inteligente cuando conectes tu correo.',
        preview_end_microcopy: 'Ahora conecta tu correo para verlo con tus datos reales.',
        preview_header: 'Bandeja inteligente',
        phase_analyzing: 'Analizando conversaciones...',
        phase_detecting: 'Detectando oportunidades...',
        phase_priorit: 'Priorizando seguimientos...',
        preview_row1_subject: 'Cotización para proyecto Q3',
        preview_row1_snippet: 'Hola, quería confirmar la propuesta antes del viernes...',
        preview_row2_subject: 'Reunión postergada',
        preview_row2_snippet: 'Te escribí hace 4 días sobre el reagendado y aún no recibo...',
        preview_row3_subject: 'Documentación pendiente de firma',
        preview_row3_snippet: 'Adjunto contrato actualizado. Necesitamos cerrarlo hoy...',
        tag_opportunity: 'Oportunidad',
        tag_no_reply: 'Sin respuesta',
        tag_for_today: 'Para hoy',
      },
      calendar: {
        title: 'Sincroniza tu calendario',
        subtitle: 'Para agendar, detectar disponibilidad\ny automatizar seguimientos.',
        connect_cta: 'Conectar Google Calendar',
        skip_cta: 'Continuar con demo',
        already_connected_title: 'Calendario ya sincronizado',
        already_connected_desc: '{{account}} ya está conectado. Avanzamos al siguiente paso.',
        preview_microcopy: 'Así Quantro coordinará tu agenda cuando conectes tu calendario.',
        preview_end_microcopy: 'Conecta tu calendario para automatizar disponibilidad y seguimientos.',
        preview_header: 'Tu día de hoy',
        phase_syncing: 'Sincronizando agenda...',
        phase_detecting: 'Detectando disponibilidad...',
        phase_aligning: 'Alineando seguimientos...',
        gap_label: 'Disponible · ideal para llamada',
        event1_title: 'Reunión con Andrea Castro',
        event1_detail: 'Google Meet · cliente actual',
        event2_title: 'Demo de producto',
        event2_detail: 'Sala virtual · prospecto',
        event3_title: 'Sync con equipo de ventas',
        event3_detail: 'Oficina · interno',
      },
      crm: {
        eyebrow_optional: 'Opcional',
        title: 'Conecta tu CRM',
        subtitle: 'Sincroniza contactos, ventas\ny automatiza seguimientos.',
        connect_cta: 'Conectar CRM',
        skip_cta: 'Continuar con demo',
        preview_microcopy: 'Así se verá tu pipeline cuando conectes tu CRM.',
        preview_end_microcopy: 'Conecta tu CRM para trabajar con tus contactos reales.',
        preview_header: 'Pipeline activo',
        phase_matching: 'Sincronizando contactos...',
        phase_staging: 'Clasificando por etapa...',
        phase_flagging: 'Detectando inactivos...',
        col_lead: 'Lead',
        col_qualified: 'Calificado',
        col_negotiation: 'Negociación',
        col_closed: 'Cerrado',
        deal_lead_1: 'Ana M. · Web nueva',
        deal_lead_2: 'Pedro T. · Consultoría',
        deal_qual_1: 'Marta R. · Plan Pro',
        deal_qual_2: 'Luis G. · Auditoría',
        deal_neg_1: 'Inversiones N. · Anual',
        deal_closed_1: 'Estudio K. · Q3',
      },
      automations: {
        eyebrow_optional: 'Opcional',
        title: 'Automatiza tu operación',
        subtitle: 'Recibe eventos desde cualquier sistema\ny deja que Quantro actúe automáticamente.',
        connect_cta: 'Configurar webhooks',
        skip_cta: 'Continuar con demo',
        preview_microcopy: 'Así Quantro reaccionará automáticamente a eventos de tu operación.',
        preview_end_microcopy: 'Configura tus webhooks para automatizar acciones reales.',
        preview_header: 'Flujo automático',
        live_indicator: 'En vivo',
        phase_listening: 'Escuchando webhooks...',
        phase_classifying: 'Clasificando eventos...',
        phase_reacting: 'Ejecutando acciones...',
        node_event_label: 'Webhook',
        node_event_title: 'Pago confirmado',
        node_event_detail: 'Stripe · checkout.session.completed',
        node_brain_label: 'Quantro',
        node_brain_title: 'Clasificado · Cliente nuevo',
        node_brain_detail: 'Confianza alta · 0.94',
        node_action_label: 'Acción',
        node_action_title: 'Crear seguimiento + agendar onboarding',
        node_action_detail: 'Notificación enviada al equipo',
      },
      ready: {
        activating: 'Activando Quantro...',
        eyebrow_real: 'En vivo',
        eyebrow_demo: 'Modo demo',
        title_real: 'Quantro ya está operando con tus datos reales',
        title_real_with_name: '{{name}}, Quantro ya está operando con tus datos reales',
        title_demo: 'Quantro está listo. Conecta tus datos reales cuando quieras',
        title_demo_with_name: '{{name}}, Quantro está listo para operar',
        subtitle_real: 'Aquí va lo que detectamos en tu operación. Empieza desde la bandeja.',
        subtitle_demo: 'Estos son datos de demostración para que veas el potencial. Conectá tu cuenta cuando quieras para reemplazarlos por información real.',
        detected_label: 'Detectamos en tu operación',
        metric_conversations: 'conversaciones activas',
        metric_opportunities: 'oportunidades de seguimiento',
        metric_contacts: 'contactos disponibles',
        metric_events: 'reuniones próximas',
        enter_cta: 'Ir a Bandeja Inteligente',
        demo_hint: 'Estás operando con datos de ejemplo. Cuando estés listo, conectá tus integraciones desde Configuración → Integraciones.',
      },
    },
    invite: {
      page_title: 'Te invitaron a un espacio',
      page_subtitle: 'Revisa los detalles antes de aceptar.',
      workspace_label: 'Espacio',
      role_label: 'Rol asignado',
      expires_label: 'Expira',
      accept_cta: 'Aceptar invitación',
      decline_cta: 'Cancelar',
      accepting: 'Aceptando...',
      success_title: 'Te uniste al espacio',
      success_subtitle: 'Te llevaremos al panel.',
      already_member: 'Ya eres miembro de este espacio.',
      error_invalid: 'Esta invitación no es válida o ha expirado.',
      error_revoked: 'Esta invitación fue revocada.',
      error_max_uses: 'Esta invitación alcanzó su límite de usos.',
      sign_in_required_title: 'Inicia sesión para continuar',
      sign_in_required_desc: 'Necesitas una cuenta de Quantro Flow para aceptar esta invitación.',
    },
    common: {
      loading: 'Cargando...',
      save: 'Guardar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      delete: 'Eliminar',
      edit: 'Editar',
      create: 'Crear',
      connect: 'Conectar',
      disconnect: 'Desconectar',
      update: 'Actualizar',
      details: 'Detalles',
      close: 'Cerrar',
      back: 'Volver',
      next: 'Siguiente',
      previous: 'Anterior',
      search: 'Buscar',
      optional: 'opcional',
      required: 'requerido',
      yes: 'Sí',
      no: 'No',
      retry: 'Reintentar',
      copy: 'Copiar',
      copied: 'Copiado',
    },

    language: {
      label: 'Idioma',
      spanish: 'Español',
      english: 'English',
      switched_to: 'Idioma cambiado a {{language}}',
    },

    simulation: {
      label: 'Modo Simulación',
      badge_simulation: 'SIMULACIÓN',
      badge_live: 'EN VIVO',
      tooltip_on: 'Estás viendo datos de muestra. Desactiva para usar tus integraciones reales.',
      tooltip_off: 'Usando datos reales de tus integraciones conectadas.',
      banner_title: 'Modo Simulación',
      banner_description_on:
        'Estás explorando Quantro Flow con un conjunto de datos de muestra realista. Tus integraciones reales no están en uso todavía.',
      banner_description_off:
        'Quantro Flow está operando con tus integraciones reales y datos en vivo.',
      confirm_title: 'Cambiar a Modo Live',
      confirm_body:
        'Vas a cambiar a Modo Live. Tus integraciones reales y los datos del workspace serán utilizados. Los datos de muestra se ocultarán.',
      confirm_go_live: 'Activar Live',
      confirm_stay: 'Permanecer en Simulación',
      toast_on: 'Modo Simulación activado',
      toast_off: 'Modo Live activado',
      toast_failed: 'No se pudo cambiar el modo',
      // Live mode empty states (shared copy across modules)
      live_empty_title: 'Sin datos reales todavía',
      live_empty_subtitle: 'Aún no hay datos reales en tu espacio de trabajo. Conecta tus integraciones o crea tu primer registro manualmente.',
      live_empty_cta_connect: 'Conectar integraciones',
      live_empty_cta_simulation: 'Activar Simulación para explorar',
      live_empty_inbox: 'Tu bandeja está vacía. Conecta Gmail u otro canal para empezar a recibir mensajes reales.',
      live_empty_crm: 'Aún no tienes contactos reales. Conecta tu CRM o agrega uno manualmente.',
      live_empty_schedule: 'Tu calendario está vacío. Conecta Google Calendar o crea tu primer evento.',
      live_empty_activity: 'Aún no hay actividad real. La actividad aparecerá aquí cuando empieces a operar.',
      live_empty_content: 'Aún no hay contenido generado. Usa el Motor de Contenido para crear tu primera pieza.',
      live_empty_onboarding: 'Aún no tienes miembros en proceso de onboarding. Agrega tu primer miembro del equipo para empezar.',
    },

    login: {
      title: 'Bienvenido a Quantro Flow',
      subtitle: 'Tu sistema operativo autónomo para el negocio.',
      sign_in_google: 'Iniciar sesión con Google',
      loading: 'Verificando credenciales...',
    },

    sidebar: {
      brand: 'Quantro Flow',
      brand_subtitle: 'BUSINESS OS',
      dashboard: 'Panel',
      smart_inbox: 'Bandeja Inteligente',
      schedule: 'Agenda',
      crm: 'CRM',
      onboarding: 'Onboarding de Equipo',
      content_engine: 'Motor de Contenido',
      automation: 'Automatización',
      members: 'Miembros',
      settings: 'Configuración',
      integrations_label: 'INTEGRACIONES',
      system_running: 'Sistema Operando',
      synced: 'Sincronizado {{time}}',
    },

    dashboard: {
      title: 'Panel',
      subtitle: 'Operaciones del negocio · Vista en tiempo real',
      live_mode: 'Datos en Vivo',
      simulation_mode: 'Modo Simulación Activo',
      kpi: {
        team: 'Equipo',
        team_active: '{{count}} activos',
        schedule: 'Reuniones Próximas',
        schedule_window: 'próximos 7 días',
        inbox: 'Solicitudes',
        inbox_processed: '{{count}} procesadas',
        crm: 'Contactos Sincronizados',
        crm_new: '{{count}} nuevos esta semana',
      },
      live_activity: 'Actividad en Vivo',
      real_time: 'Tiempo Real',
      ai_suggestions: 'Sugerencias IA',
      quick_actions: 'Acciones Rápidas',
      today_meetings: 'Reuniones de Hoy',
      no_meetings: 'Sin reuniones hoy',
      view_full_calendar: 'Ver Calendario Completo',
      ai_engine: 'Motor IA',
      running: 'Operando',
      active: 'Activo',
      no_recent_activity: 'Sin actividad reciente',
      activity_hint: 'La actividad aparecerá aquí cuando el sistema procese items',
      no_suggestions: 'Sin sugerencias aún',
      integration_connected: '{{count}} Integración Conectada',
      integrations_connected: '{{count}} Integraciones Conectadas',
      integration_sync_status: 'Sincronización en tiempo real',
      manage: 'Gestionar',
    },

    system_health: {
      title: 'Estado del Sistema',
      tagline: 'Quantro Flow detecta y resuelve problemas antes de que los notes.',
      status_healthy: 'Sistema Saludable',
      status_repaired: 'Sistema Auto-Reparado',
      status_degraded: 'Sistema Degradado',
      all_operational: 'Todos los sistemas operativos',
      last_check: 'Última verificación: {{time}}',
      subcopy_healthy:
        'Quantro Flow mantiene activamente tus integraciones. Las inconsistencias se detectan y resuelven automáticamente.',
      subcopy_repaired:
        'Quantro Flow detectó componentes faltantes y los reparó automáticamente.',
      subcopy_degraded:
        'Algunos componentes requieren atención. Quantro Flow está trabajando en resolverlos.',
      checks: {
        integrations_stable: 'Integraciones estables',
        integrations_stable_ok: '{{have}}/{{total}} proveedores registrados',
        integrations_stable_fail: 'Faltan proveedores ({{missing}})',
        data_consistency: 'Consistencia de datos verificada',
        data_consistency_ok: 'Perfil de negocio presente',
        data_consistency_fail: 'Perfil de negocio ausente',
        no_issues: 'Sin problemas detectados',
        no_issues_ok: 'Todos los sistemas operativos',
        no_issues_repairs: '{{count}} reparación(es) en el último arranque',
        issues_detected: 'Problemas detectados',
      },
      auto_resolved_header: 'Resueltos automáticamente en el último arranque',
      repair_provider_restored:
        "Integración '{{name}}' faltante fue restaurada automáticamente.",
      repair_metadata_backfilled:
        "Metadata reparada para '{{name}}' ({{fields}}).",
      repair_toast_single: 'El sistema reparó una integración faltante automáticamente',
      repair_toast_multi: 'El sistema reparó {{count}} integraciones faltantes automáticamente',
    },

    settings: {
      title: 'Configuración',
      subtitle: 'Configura tu Business OS',
      tabs: {
        integrations: 'Integraciones',
        automation: 'Automatización',
        business_profile: 'Perfil del Negocio',
        workspace: 'Espacio de Trabajo',
      },
      automation: {
        heading: 'Automatización',
        description:
          'Configura tus políticas de automatización, umbrales de confianza y reglas de escalamiento.',
        manage_button: 'Gestionar Políticas de Automatización',
        overview_title: 'Resumen Rápido',
        overview_auto: 'Auto-ejecución habilitada para items de alta confianza',
        overview_manual: 'Aprobación manual requerida para confianza media',
        overview_escalation: 'Escalamiento activado para baja confianza o conflictos',
      },
      profile: {
        heading: 'Perfil del Negocio',
        description: 'Configura el tipo de negocio y personaliza la terminología del sistema',
        industry_label: 'Industria',
        industry_helper:
          'El sistema adaptará su lenguaje y sugerencias IA según tu industria',
        use_case_label: 'Caso de Uso (opcional)',
        use_case_placeholder: 'Describe cómo usas este Business OS...',
        naming_heading: 'Nombres Personalizados de Entidades',
        naming_helper:
          'Personaliza la terminología en todo el sistema. Deja en blanco para usar los valores por defecto de la industria.',
        label_contacts: 'Etiqueta de Contactos',
        label_team: 'Etiqueta de Miembros del Equipo',
        label_meetings: 'Etiqueta de Reuniones',
        label_events: 'Etiqueta de Eventos',
        label_services: 'Etiqueta de Servicios/Productos',
        simulation_heading: 'Modo Simulación',
        simulation_description:
          'Carga datos operativos de muestra realistas para tu industria. Ideal para probar flujos de trabajo, validar automatizaciones y hacer demos antes de conectar integraciones reales.',
        simulation_active: 'Activo: El sistema está poblado con datos simulados',
        save_button: 'Guardar Perfil del Negocio',
        saving: 'Guardando...',
        saved_toast: 'Perfil del Negocio actualizado correctamente',
        save_failed_toast: 'Error al actualizar el Perfil del Negocio',
      },
      workspace: {
        heading: 'Espacio de Trabajo',
        description: 'Configuración del workspace y gestión del equipo',
        name_label: 'Nombre del Workspace',
        name_placeholder: 'Mi Negocio',
        phase_note:
          'Las funciones de autenticación y workspace multi-tenant estarán disponibles en la Fase 6.',
      },
    },

    integrations: {
      groups: {
        ai: 'IA e Inteligencia',
        email: 'Correo y Calendario',
        crm: 'CRM',
        automation: 'Webhooks y Endpoints',
      },
      status: {
        connected: 'Conectado',
        not_connected: 'No Conectado',
        last_sync: 'Última sincronización: {{time}}',
      },
      actions: {
        save_and_connect: 'Guardar y Conectar',
        connect_google: 'Conectar con Google',
        enable_webhooks: 'Activar Webhooks',
        test_connection: 'Probar Conexión',
        update_config: 'Actualizar Configuración',
        disconnect: 'Desconectar',
      },
      openai: {
        name: 'Proveedor OpenAI / LLM',
        description:
          'Potencia la clasificación IA, redacción y auto-ejecución en todos tus flujos.',
        helper:
          'Este workspace está alimentado por la Clave Universal Emergent. Añade tu propia clave para sobrescribirla por workspace.',
        api_key: 'API Key',
        default_model: 'Modelo Predeterminado',
      },
      gmail: {
        name: 'Gmail',
        description:
          'Sincroniza el correo entrante hacia la Bandeja Inteligente y permite al sistema redactar respuestas.',
        account: 'Cuenta Conectada',
      },
      calendar: {
        name: 'Google Calendar',
        description:
          'Sincronización bidireccional de eventos, reservas y ventanas de disponibilidad.',
        calendar_id: 'ID del Calendario',
      },
      crm: {
        name: 'Sistema CRM',
        description:
          'Sincronización bidireccional con HubSpot, GoHighLevel, Pipedrive o cualquier CRM con API.',
        provider: 'Proveedor CRM',
        api_key: 'API Key',
        base_url: 'URL Base (opcional)',
      },
      webhook: {
        name: 'Webhooks Entrantes',
        description:
          'Reenvía eventos desde cualquier servicio hacia Business OS. Usa el endpoint a continuación en tus herramientas externas.',
        endpoint_label: 'TU ENDPOINT ENTRANTE',
        shared_secret: 'Secreto Compartido (opcional)',
        secret_placeholder: 'Genera una cadena aleatoria',
        coming_soon_badge: 'Próximamente',
        coming_soon_description: 'Los webhooks entrantes todavía no están disponibles. Estamos trabajando en esta función.',
      },
      real_connect: {
        title: 'Conectar Gmail / Calendar',
        description: 'La conexión real de Gmail y Google Calendar se hace por OAuth, no con un formulario. Usa el flujo de bienvenida para autorizar tu cuenta de Google.',
        cta: 'Conectar bandeja real',
      },
      toasts: {
        connected: '{{provider}} conectado exitosamente',
        disconnected: '{{provider}} desconectado',
        test_ok: 'La conexión de {{provider}} está saludable',
        test_fail: '{{provider}} no está conectado',
        load_failed: 'Error al cargar integraciones',
        connect_failed: 'Error al conectar {{provider}}',
        endpoint_copied: 'Endpoint copiado al portapapeles',
        copy_failed: 'Copia no disponible en este navegador — selecciona y copia manualmente',
      },
    },

    data_mode_banner: {
      demo_title_inbox: 'Modo demo · Bandeja',
      demo_title_schedule: 'Modo demo · Agenda',
      demo_desc_inbox: 'Estás viendo correos de muestra. Conecta tu bandeja para trabajar con tus datos reales.',
      demo_desc_schedule: 'Estás viendo eventos de muestra. Conecta tu calendario para coordinar tu agenda real.',
      demo_cta_inbox: 'Conectar bandeja',
      demo_cta_schedule: 'Conectar calendario',
      real_title: 'Sincronizado con {{provider}}',
      last_sync_label: 'Última sincronización {{when}}',
      last_sync_never: 'aún sin sincronizar',
      last_sync_error_label: 'La última sincronización falló — vamos a reintentarlo automáticamente',
      paused_badge: 'Pausado',
      sync_now: 'Sincronizar',
      syncing: 'Sincronizando…',
      sync_success_title: 'Sincronización completada',
      sync_success_desc: '{{emails}} correos y {{events}} eventos desde {{provider}}.',
      sync_failed_title: 'No pudimos sincronizar',
      sync_failed_desc: 'Inténtalo de nuevo en unos segundos.',
    },

    sync_preference: {
      title: '¿Cómo quieres sincronizar tu bandeja?',
      description: 'Ya importamos y clasificamos tus primeros correos. Elige cómo quieres mantenerla actualizada de aquí en adelante.',
      auto_title: 'Automático (cada 15 min)',
      auto_desc: 'Los correos nuevos se importan y clasifican solos, sin que hagas nada.',
      manual_title: 'Manual (yo decido cuándo)',
      manual_desc: 'Solo se sincroniza cuando tú presionas "Sincronizar". Útil para controlar el consumo de créditos IA.',
      footer_hint: 'Puedes cambiar esto luego en Configuración.',
      toast_auto: 'Sincronización automática activada',
      toast_manual: 'Sincronización manual activada',
      toast_error: 'No pudimos guardar tu preferencia, inténtalo de nuevo',
    },

    smart_inbox: {
      title: 'Bandeja Inteligente',
      subtitle: 'Clasificación IA y triage automatizado',
      empty_state: 'No hay items en la bandeja',
      empty_hint: 'Tus solicitudes entrantes aparecerán aquí',
      empty_filter_title: 'Nada por aquí todavía',
      empty_filter_processed: 'Aún no has clasificado ningún mensaje. Ve a la pestaña "New" y usa "Classify" para analizarlos.',
      empty_filter_actioned: 'Todavía no hay mensajes completados.',
      empty_filter_declined: 'Todavía no has omitido ningún mensaje.',
      empty_filter_new: '¡Estás al día! No hay mensajes nuevos por clasificar.',
      empty_filter_cta: 'Ver todos los mensajes',
      analyze: 'Analizar',
      analyze_all: 'Analizar todo',
      approve: 'Aprobar',
      escalate: 'Escalar',
      filters: {
        all: 'Todas',
        unread: 'Sin leer',
        needs_review: 'Requieren revisión',
        auto_actioned: 'Auto-ejecutadas',
        escalated: 'Escaladas',
        processed: 'Procesadas',
      },
      intent: {
        booking: 'Reserva',
        onboarding: 'Onboarding',
        follow_up: 'Seguimiento',
        inquiry: 'Consulta',
        escalation: 'Escalamiento',
        spam: 'Spam',
        needs_review: 'Requiere revisión',
      },
      status: {
        new: 'Nueva',
        analyzed: 'Analizada',
        needs_review: 'Requiere revisión',
        auto_actioned: 'Auto-ejecutada',
        escalated: 'Escalada',
        processed: 'Procesada',
      },
      confidence_label: 'Confianza',
      suggested_action: 'Acción sugerida',
      execution_trail: 'Registro de Ejecución',
      analyzing: 'Analizando...',
      batch_selected: '{{count}} seleccionada(s)',
      batch_analyze: 'Analizar seleccionadas',
      clear_selection: 'Limpiar selección',
      process_inbox: 'Procesar Bandeja',
      view_details: 'Ver detalles',
      mark_processed: 'Marcar procesada',
      escalation_reason: 'Razón del escalamiento',
      toasts: {
        analyzed: 'Item analizado correctamente',
        analyze_failed: 'Error al analizar',
        batch_done: '{{count}} items analizados',
        batch_failed: 'Error en análisis por lote',
        marked_processed: 'Item marcado como procesado',
        mark_failed: 'Error al marcar item',
        escalated: 'Item escalado',
      },
    },

    crm: {
      title: 'CRM',
      subtitle: 'Contactos y relaciones',
      empty_state: 'Sin contactos aún',
      select_prompt: 'Selecciona un contacto para ver sus detalles',
      empty_hint: 'Tus contactos sincronizados aparecerán aquí',
      search_placeholder: 'Buscar contactos...',
      add_contact: 'Añadir Contacto',
      new_contact: 'Nuevo Contacto',
      table: {
        name: 'Nombre',
        email: 'Correo',
        phone: 'Teléfono',
        status: 'Estado',
        source: 'Origen',
        created: 'Creado',
        last_contact: 'Último contacto',
        actions: 'Acciones',
      },
      status: {
        lead: 'Lead',
        contacted: 'Contactado',
        qualified: 'Calificado',
        customer: 'Cliente',
        churned: 'Inactivo',
        active: 'Activo',
      },
      details: {
        title: 'Detalles del Contacto',
        edit: 'Editar',
        delete: 'Eliminar',
        activities: 'Actividades',
        notes: 'Notas',
      },
      form: {
        name_label: 'Nombre completo',
        email_label: 'Correo electrónico',
        phone_label: 'Teléfono',
        status_label: 'Estado',
        source_label: 'Origen',
        notes_label: 'Notas',
      },
      toasts: {
        created: 'Contacto creado',
        updated: 'Contacto actualizado',
        deleted: 'Contacto eliminado',
        save_failed: 'Error al guardar contacto',
      },
    },

    schedule: {
      title: 'Agenda',
      subtitle: 'Reuniones y eventos',
      empty_state: 'Sin eventos programados',
      empty_hint: 'Tus próximas reuniones aparecerán aquí',
      add_event: 'Añadir Evento',
      today: 'Hoy',
      tomorrow: 'Mañana',
      this_week: 'Esta semana',
      upcoming: 'Próximas',
      past: 'Pasadas',
      duration: '{{minutes}} min',
      attendees: '{{count}} asistentes',
      with: 'con {{person}}',
      status: {
        scheduled: 'Programada',
        completed: 'Completada',
        cancelled: 'Cancelada',
        no_show: 'No asistió',
      },
      form: {
        title_label: 'Título',
        date_label: 'Fecha',
        time_label: 'Hora',
        duration_label: 'Duración (minutos)',
        location_label: 'Ubicación',
        attendees_label: 'Asistentes',
        notes_label: 'Notas',
      },
      toasts: {
        created: 'Evento creado',
        updated: 'Evento actualizado',
        cancelled: 'Evento cancelado',
        save_failed: 'Error al guardar evento',
      },
    },

    content_engine: {
      title: 'Motor de Contenido',
      subtitle: 'Generación de contenido con IA',
      tabs: {
        generate: 'Generar',
        templates: 'Plantillas',
        history: 'Historial',
      },
      generate: {
        topic_label: 'Tema',
        topic_placeholder: 'Ej: lanzamiento de nuevo producto...',
        tone_label: 'Tono',
        type_label: 'Tipo de contenido',
        generate_button: 'Generar Contenido',
        regenerate: 'Regenerar',
        copy_button: 'Copiar',
        generating: 'Generando contenido...',
        social_post: 'Post Social',
        email_draft: 'Borrador de Correo',
        hashtags: 'Hashtags',
        subject: 'Asunto',
        body: 'Cuerpo',
        call_to_action: 'Call to Action',
      },
      tone: {
        professional: 'Profesional',
        friendly: 'Amigable',
        warm: 'Cálido',
        urgent: 'Urgente',
        playful: 'Divertido',
      },
      templates: {
        heading: 'Plantillas',
        new_template: 'Nueva Plantilla',
        edit: 'Editar',
        delete: 'Eliminar',
        use: 'Usar',
        empty: 'Sin plantillas aún',
        variables: 'Variables',
        category_label: 'Categoría',
        type_label: 'Tipo',
        name_label: 'Nombre',
        content_label: 'Contenido',
        save: 'Guardar Plantilla',
      },
      history: {
        heading: 'Historial de Generación',
        empty: 'Sin generaciones aún',
        empty_hint: 'Tu contenido generado aparecerá aquí',
      },
      toasts: {
        generated: 'Contenido generado',
        generate_failed: 'Error al generar contenido',
        copied: 'Copiado al portapapeles',
        copy_failed: 'Error al copiar',
        template_saved: 'Plantilla guardada',
        template_deleted: 'Plantilla eliminada',
        save_failed: 'Error al guardar',
      },
    },

    onboarding: {
      // Onboarding page (existing — customer onboarding flows)
      title: 'Onboarding',
      subtitle: 'Flujos de incorporación de clientes',
      empty_state: 'Sin flujos de onboarding activos',
      empty_hint: 'Los onboardings creados aparecerán aquí',
      new_flow: 'Nuevo Flujo',
      progress: 'Progreso',
      steps: 'Pasos',
      step_completed: 'Completado',
      step_pending: 'Pendiente',
      step_in_progress: 'En progreso',
      start: 'Iniciar',
      complete: 'Completar',
      resume: 'Continuar',
      cancel: 'Cancelar',
      form: {
        client_label: 'Cliente',
        type_label: 'Tipo de onboarding',
        start_date_label: 'Fecha de inicio',
      },
      toasts: {
        created: 'Flujo de onboarding creado',
        updated: 'Flujo actualizado',
        completed: 'Flujo completado',
        step_completed: 'Paso completado',
        save_failed: 'Error al guardar',
      },

      // Phase 7b-ext — Members → Onboarding mini-tab
      tab_label: 'Onboarding',
      summary: 'Seguimiento visual del avance de incorporación de cada miembro.',
      member_progress: 'Progreso del miembro',
      status_pending: 'Pendiente',
      status_in_progress: 'En progreso',
      status_completed: 'Completado',
      status_blocked: 'Bloqueado',
      step_invitation_sent: 'Invitación enviada',
      step_invitation_sent_done: 'Este miembro ya recibió la invitación.',
      step_invitation_sent_pending: 'Genera un enlace para invitarlo.',
      step_account_created: 'Cuenta creada',
      step_account_created_done: 'La cuenta ya fue creada correctamente.',
      step_account_created_pending: 'La cuenta todavía no ha sido creada.',
      step_companies_assigned: 'Empresas asignadas',
      step_companies_assigned_done: 'El miembro ya tiene empresas asignadas.',
      step_companies_assigned_pending: 'Falta asignar empresa antes de activar el acceso.',
      step_role_configured: 'Rol configurado',
      step_role_configured_done: 'El rol del miembro ya fue configurado.',
      step_role_configured_pending: 'Sigue como visualizador hasta asignarle un rol.',
      step_first_login: 'Primer acceso',
      step_first_login_done: 'El miembro ya ingresó por primera vez.',
      step_first_login_pending: 'Aún no ha iniciado sesión.',
      action_copy_invite: 'Copiar enlace de invitación',
      action_resend_invite: 'Reenviar invitación',
      action_mark_complete: 'Marcar onboarding completo',
      action_revoke_access: 'Revocar acceso',
      empty: 'No hay miembros para mostrar.',
      mark_complete_confirm: '¿Marcar el onboarding de este miembro como completo?',
    },

    automation: {
      title: 'Políticas de Automatización',
      subtitle: 'Reglas de auto-ejecución y escalamiento',
      tabs: {
        policies: 'Políticas',
        escalations: 'Escalamientos',
      },
      policies: {
        heading: 'Políticas de Confianza',
        description:
          'Define qué ocurre según el nivel de confianza del análisis IA.',
        high_confidence: 'Alta Confianza',
        medium_confidence: 'Confianza Media',
        low_confidence: 'Baja Confianza',
        threshold_label: 'Umbral',
        action_label: 'Acción',
        enabled_label: 'Habilitada',
        new_policy: 'Nueva Política',
        save: 'Guardar Política',
        action_auto: 'Auto-ejecutar',
        action_manual: 'Aprobación manual',
        action_review: 'Requiere revisión',
        action_escalate: 'Escalar',
      },
      escalations: {
        heading: 'Reglas de Escalamiento',
        description:
          'Condiciones avanzadas que fuerzan revisión o notificación del equipo.',
        new_rule: 'Nueva Regla',
        rule_type: 'Tipo de Regla',
        condition: 'Condición',
        priority: 'Prioridad',
        notify_team: 'Notificar al equipo',
        types: {
          intent: 'Intención',
          keyword: 'Palabra clave',
          calendar_conflict: 'Conflicto de calendario',
          incomplete_entities: 'Entidades incompletas',
          urgency: 'Urgencia',
          contact_type: 'Tipo de contacto',
        },
        priority_levels: {
          low: 'Baja',
          medium: 'Media',
          high: 'Alta',
          critical: 'Crítica',
        },
      },
      toasts: {
        policy_saved: 'Política guardada',
        policy_deleted: 'Política eliminada',
        rule_saved: 'Regla guardada',
        rule_deleted: 'Regla eliminada',
        save_failed: 'Error al guardar',
      },
    },

    // ────────────────────────────────────────────────────
    // Decisiones — patrón clave para el sistema de decisiones
    // ────────────────────────────────────────────────────
    decisions: {
      revenue: {
        raise_prices: {
          title: 'Subir precios un {{percent}}%',
          summary:
            'La demanda y márgenes actuales respaldan un ajuste de precios del {{percent}}%.',
          impact: 'Incremento estimado en ingresos de {{lift}}%.',
          action_label: 'Aplicar cambio de precios',
        },
        launch_campaign: {
          title: 'Lanzar campaña de retención',
          summary:
            '{{count}} clientes muestran señales de riesgo — una campaña dirigida puede recuperarlos.',
          action_label: 'Lanzar campaña',
        },
      },
      action_center: {
        no_pending: 'Sin decisiones pendientes',
        pending_count: '{{count}} decisión pendiente',
        pending_count_plural: '{{count}} decisiones pendientes',
      },
    },

    // ────────────────────────────────────────────────────
    // Agentes — etiquetas y descripciones
    // ────────────────────────────────────────────────────
    agents: {
      pricing: {
        label: 'Agente de Precios',
        description: 'Monitorea márgenes y recomienda ajustes de precios.',
      },
      retention: {
        label: 'Agente de Retención',
        description: 'Detecta clientes en riesgo y propone campañas.',
      },
      triage: {
        label: 'Agente de Triage',
        description: 'Clasifica y enruta items entrantes de la bandeja.',
      },
    },
  },

  // ────────────────────────────────────────────────────────────
  // English
  // ────────────────────────────────────────────────────────────
  en: {
    // Plan & Usage (new section)
    onboarding_lite: {
      title: 'Set up your account',
      title_with_name: 'Hi {{name}}, set up your account',
      subtitle: 'Three quick answers to tailor Quantro Flow to your operation.',
      country_label: 'Country where your company operates',
      country_hint: 'We use this for currency, time zone and local regulations.',
      country_placeholder: 'Select a country',
      industry_label: 'Your company industry',
      industry_hint: 'This personalizes the AI agent recommendations.',
      industry_placeholder: 'Select an industry',
      industries: {
        real_estate: 'Real estate',
        healthcare: 'Healthcare / Clinics',
        consulting: 'Consulting',
        ecommerce: 'E-commerce',
        technology: 'Technology / Software',
        finance: 'Finance / Insurance',
        education: 'Education',
        manufacturing: 'Manufacturing',
        retail: 'Retail',
        hospitality: 'Hospitality / Restaurants',
        legal: 'Legal',
        marketing: 'Marketing / Agency',
        construction: 'Construction',
        agriculture: 'Agriculture',
        automotive: 'Automotive',
        logistics: 'Logistics',
        nonprofit: 'Nonprofit',
        other: 'Other',
      },
      company_label: 'Company name',
      company_hint: 'Used in your workspace and in emails the agent sends.',
      company_placeholder: 'e.g. Acme Consulting',
      continue_to_app: 'Continue to the platform',
      saving: 'Saving…',
      saved_title: 'Setup saved',
      saved_desc: 'Quantro Flow is now tailored to your operation.',
      save_failed: 'We couldn\u2019t save. Please try again.',
      fill_all_fields: 'Please fill in all three fields to continue.',
    },
    plan_usage: {
      nav_label: 'Plan & Usage',
      page_title: 'Plan & Usage',
      page_subtitle: 'Your plan, consumption and account in one place',
      current_plan: 'Current plan',
      plan_name: 'Plan',
      status_active: 'Active',
      status_trial: 'Trial',
      status_expired: 'Expired',
      renews_on: 'Renews on',
      next_billing: 'Next billing',
      view_plan: 'View my plan',
      upgrade_plan: 'Upgrade plan',
      api_usage: 'API Usage',
      api_calls_this_month: 'Calls this month',
      monthly_limit: 'Monthly limit',
      usage_percent: '{{percent}}% used',
      overage: 'Overage',
      breakdown_title: 'Breakdown by module',
      inbox_ai: 'Inbox AI',
      crm: 'CRM',
      content_engine: 'Content Engine',
      automations: 'Automations',
      calls: 'calls',
      share: '{{pct}}% of total',
      billing: 'Billing',
      payment_method: 'Payment method',
      no_payment_method: 'No payment method',
      manage_payments: 'Manage payments',
      amount_due: 'Amount due',
      account: 'Account',
      user: 'User',
      workspace: 'Workspace',
      switch_account: 'Switch account',
      signout: 'Sign out',
      view_account: 'View account',
      source_supabase: 'Live data from Supabase · ai_usage',
      plan_unavailable: 'Plan not available',
      refresh: 'Refresh',
      no_usage_yet: 'No usage yet this month.',
      limit_blocked: 'You need an active plan to use Quantro Flow\u2019s smart features.',
      limit_coupon: 'Your plan has a coupon applied · 500 monthly calls.',
      limit_test_user: 'Internal QA account · maximum access.',
      // AI Credits (USD-based)
      ai_credits: 'AI Credits',
      ai_credits_subtitle: 'USD balance deducted by the real cost of every request.',
      credits_unit: 'USD',
      credits_used: 'Used: {{used}}',
      credits_consumed: 'Consumed',
      credits_source_quantro: 'Quantro API · gpt-4o-mini',
      credits_source_user: 'Your own API key',
      credits_source_blocked: 'Out of credits',
      credits_low_warning: 'Less than 20% of your AI credits remaining. Consider upgrading your plan or adding your own API key.',
      credits_fallback_user_key: 'Using your own OpenAI key. Quantro is not deducting credits.',
      credits_blocked_message: 'Your AI credits are exhausted. Add your own OpenAI API key or upgrade to keep using smart features.',
      add_own_api_key: 'Add my API key',
      credits_pricing_hint: 'Quantro forces gpt-4o-mini when using your credits · input $0.15/1M · output $0.60/1M tokens.',
    },
    billing: {
      select_plan_title: 'Choose your plan',
      select_plan_subtitle: 'Manage your subscription, payment method and billing.',
      select_plan_cta: 'Choose plan',
      current_plan_cta: 'Current plan',
      current_plan_badge: 'Current',
      most_popular: 'Most popular',
      period_monthly: 'Monthly',
      period_annual: 'Annual',
      annual_save: 'Save 20%',
      annual_perk: '· 2 months free',
      annual_perk_microcopy: 'Equivalent to 2 months free when billed annually.',
      per_month_short: 'mo',
      billed_annually: 'Billed annually · ${{total}} USD',
      upgrade_plan: 'Upgrade plan',
      manage_subscription: 'Manage subscription',
      change_plan: 'Change plan',
      redirecting: 'Redirecting…',
      checkout_failed: 'Could not start checkout. Please try again.',
      portal_failed: 'Could not open customer portal.',
      checkout_success_title: 'Payment confirmed!',
      checkout_success_desc: 'Activating your plan. This may take a few seconds.',
      checkout_cancelled_title: 'Payment cancelled',
      checkout_cancelled_desc: 'No charges were made.',
      microcopy: 'Manage your subscription, payment method and invoices in one place.',
      secured_by_stripe: 'Payments processed securely by Stripe',
      error_hint: 'Open your browser console (F12 → Console) for the full error. Also check Supabase Dashboard → Edge Functions → create-checkout-session → Logs.',
    },
    automation_policies: {
      page_title: 'Automation Policies',
      page_subtitle: 'Define how AI acts on each intent and when to escalate to humans',
      tab_policies: 'Intent Policies',
      tab_rules: 'Escalation Rules',
      new_policy: 'New policy',
      new_rule: 'New rule',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      confirm_delete_title: 'Delete this item?',
      confirm_delete_desc: 'This action cannot be undone.',
      active: 'Active',
      inactive: 'Inactive',
      empty_policies_title: 'No policies yet',
      empty_policies_desc: 'Create your first policy to automate actions based on detected intent.',
      empty_rules_title: 'No escalation rules yet',
      empty_rules_desc: 'Define when AI should hand off to a human.',
      policy_name: 'Name',
      policy_intent: 'Intent type',
      policy_mode: 'Mode',
      policy_confidence: 'Confidence threshold',
      policy_daily_limit: 'Daily limit',
      policy_status: 'Status',
      mode_auto_execute: 'Auto-execute',
      mode_require_approval: 'Require approval',
      mode_suggest_only: 'Suggest only',
      intent_new_lead: 'New lead',
      intent_inbound_msg: 'Inbound message',
      intent_follow_up: 'Follow-up',
      intent_schedule: 'Scheduling',
      intent_crm_update: 'CRM update',
      intent_content_gen: 'Content generation',
      intent_onboarding: 'Onboarding task',
      intent_human_escalation: 'Human escalation',
      rule_name: 'Name',
      rule_condition: 'Condition',
      rule_action: 'Action',
      rule_threshold: 'Threshold (optional)',
      rule_status: 'Status',
      cond_low_confidence: 'Low confidence',
      cond_vip_user: 'VIP user',
      cond_complaint: 'Complaint detected',
      cond_missing_data: 'Missing data',
      cond_integration_fail: 'Integration failure',
      action_send_human: 'Send to human',
      action_confirm: 'Request confirmation',
      action_retry: 'Retry',
      action_hold_review: 'Hold for review',
      saved_toast: 'Saved successfully',
      deleted_toast: 'Deleted',
      save_failed: 'Could not save',
    },

    auth: {
      sign_in_title: 'Welcome to Quantro Flow',
      sign_in_subtitle: 'Your Business Operating System. Sign in to continue.',
      sign_in_with_google: 'Continue with Google',
      signing_in: 'Signing in…',
      signing_out: 'Signing out…',
      signed_out_title: 'You have been signed out',
      signed_out_subtitle: 'Come back anytime.',
      login_failed_title: 'Sign-in failed',
      login_failed_desc: 'Please try again or contact support.',
      welcome_back: 'Welcome back, {{name}}',
      logout: 'Sign out',
      my_workspace: 'My Workspace',
      switch_workspace: 'Switch workspace',
      active_workspace: 'Active workspace',
      you_are_owner: 'Owner',
      secure_session: 'Secure session',
      secured_by: 'Secured by Supabase Auth',
      no_workspace_title: 'No workspace yet',
      no_workspace_subtitle: 'Create one to start operating.',
      new_workspace: 'New workspace',
      email_label: 'Email',
      email_placeholder: 'you@company.com',
      password_label: 'Password',
      password_placeholder: 'Your password',
      full_name_label: 'Full name',
      full_name_placeholder: 'First and last name',
      full_name_invalid: 'Enter your first and last name',
      sign_in_cta: 'Sign in',
      sign_up_cta: 'Create account',
      toggle_to_signup: "Don't have an account? Create one",
      toggle_to_signin: 'Already have an account? Sign in',
      forgot_password: 'Forgot your password?',
      signup_success_title: 'Check your email',
      signup_success_desc: 'We sent a confirmation link to activate your account.',
      password_min: 'Password must be at least 8 characters.',
      missing_fields: 'Enter both email and password.',
      account_confirmed: 'Account confirmed',
    },
    members: {
      title: 'Members & Permissions',
      subtitle: 'Manage who has access to this workspace and what they can do.',
      tab_members: 'Members',
      tab_invites: 'Invites',
      your_role: 'Your role',
      empty_invites: 'No active invites. Generate a link to invite your team.',
      invite_link_created: 'Invite link created',
      invite_link_copied: 'Link copied to clipboard',
      copy_link: 'Copy link',
      revoke: 'Revoke',
      revoked: 'Revoked',
      revoke_confirm: 'Revoke this invite? The link will stop working.',
      remove_member: 'Remove member',
      remove_confirm: 'Remove this member from the workspace?',
      leave_workspace: 'Leave workspace',
      leave_confirm: 'Leave this workspace? You will lose access to its data.',
      change_role: 'Change role',
      generate_invite: 'Generate link',
      generating: 'Generating...',
      invite_role_label: 'Role for the invited user',
      invite_max_uses: 'Max uses',
      invite_expires: 'Expires in (days)',
      invite_email_optional: 'Email (optional)',
      invite_full_name_optional: 'Full name (optional)',
      invite_create_title: 'New invite',
      member_count: '{{count}} members',
      pending_count: '{{count}} pending',
      transfer_ownership: 'Transfer ownership',
      transfer_warning: 'Transferring ownership will demote you to Admin. Continue?',
      role_owner: 'Owner',
      role_admin: 'Admin',
      role_manager: 'Manager',
      role_operator: 'Operator',
      role_agent: 'Agent',
      role_owner_desc: 'Full control. One per workspace. Can transfer ownership.',
      role_admin_desc: 'Manage members, integrations, profile, simulation.',
      role_manager_desc: 'Edit automation policies, escalation rules, templates.',
      role_operator_desc: 'Day-to-day usage: inbox, schedule, CRM, content gen.',
      role_agent_desc: 'Read-only on most modules.',
      // Quantro role taxonomy (matches Supabase org_members)
      role_leader: 'Leader',
      role_accountant: 'Accountant',
      role_member: 'Member',
      role_viewer: 'Viewer',
      role_leader_desc: 'Manage members, integrations, profile, simulation.',
      role_accountant_desc: 'Edit automation policies, escalation rules, templates.',
      role_member_desc: 'Day-to-day usage: inbox, schedule, CRM, content gen.',
      role_viewer_desc: 'Read-only on most modules.',
      forbidden_title: 'Action not allowed',
      forbidden_desc: 'Your current role does not have permission for this action.',
      not_owner_promotion: 'Only the Owner can assign Admin or Owner roles.',
    },
    audit: {
      tab_label: 'Audit log',
      empty: 'No events recorded yet.',
      action_invitation_created: 'Invitation created',
      action_invitation_accepted: 'Invitation accepted',
      action_access_revoked: 'Access revoked',
      action_user_deleted: 'User deleted',
      action_role_changed: 'Role changed',
      action_companies_assigned: 'Company assigned',
      action_companies_removed: 'Company removed',
      action_permissions_modified: 'Permissions modified',
      action_onboarding_completed: 'Onboarding completed',
      action_unknown: 'System event',
      time_just_now: 'just now',
      time_minutes_ago: '{{count}}m ago',
      time_hours_ago: '{{count}}h ago',
      time_days_ago: '{{count}}d ago',
      filter_all: 'All',
      filter_member: 'Member',
      load_more: 'Load more',
      header_actor: 'Actor',
      header_target: 'Target',
      action_added: 'Member added',
      filter_start_date: 'From',
      filter_end_date: 'To',
      filter_action: 'Action',
      filter_action_all: 'All actions',
      export_button: 'Export',
      export_menu_label: 'Export format',
      export_csv: 'Download CSV',
      export_json: 'Download JSON',
      export_success: 'Audit log exported as {{format}}',
      export_error: 'Could not export the audit log',
    },
    workspace: {
      rename_button: 'Rename workspace',
      rename_title: 'Rename workspace',
      rename_description: 'Customize the name shown to every member. You can change it any time.',
      rename_label: 'Workspace name',
      rename_placeholder: 'e.g. My Company',
      rename_hint: 'Between 1 and 80 characters. Changes are recorded in the audit log.',
      rename_save: 'Save',
      rename_success: 'Workspace renamed to "{{name}}"',
      rename_error: 'Could not rename the workspace',
      rename_empty: 'Name cannot be empty',
    },
    welcome: {
      sign_out: 'Sign out',
      progress_aria: 'Activation progress',
      preview: {
        badge_preview: 'Preview',
        badge_demo: 'Demo mode',
        badge_real: 'Real data',
        badge_connecting: 'Connecting',
        connecting: 'Connecting...',
        continue_demo: 'Continue with demo',
        real_pending_title: 'Real connection coming soon',
        real_pending_desc: 'You\u2019ll be able to connect your Google account shortly. We\u2019re staying in demo mode for now.',
        real_pending_desc_crm: 'Real CRM integration is coming. We\u2019re staying in demo mode for now.',
        real_pending_desc_webhooks: 'You\u2019ll be able to set up real webhooks shortly. We\u2019re staying in demo mode for now.',
        real_failed_title: 'We couldn\u2019t connect with Google',
        real_connected_title: 'Google connected!',
        real_connected_title_v2: '{{provider}} connected!',
        real_synced_desc: 'Synced {{emails}} emails and {{events}} events from {{account}}.',
        real_connected_no_sync_title: 'Google connected, sync pending',
        real_connected_no_sync_title_v2: '{{provider}} connected, sync pending',
        real_connected_no_sync_desc: 'The account is linked but the initial sync failed. We\u2019ll retry in the background.',
      },
      connect_modal: {
        title_inbox: 'Pick your email provider',
        title_calendar: 'Pick your calendar provider',
        desc_inbox: 'Connect Gmail or Outlook. We sync your inbox and prioritize it automatically.',
        desc_calendar: 'Connect Google Calendar or Outlook Calendar. We detect availability and automate follow-ups.',
        google_title: 'Google',
        google_subtitle: 'Gmail \u00b7 Google Calendar',
        microsoft_title: 'Microsoft',
        microsoft_subtitle: 'Outlook \u00b7 Calendar',
        google_failed_title: 'We couldn\u2019t connect with Google',
        microsoft_failed_title: 'We couldn\u2019t connect with Microsoft',
        google_unavailable_desc: 'Google integration isn\u2019t configured for this workspace yet. Ping your admin.',
        microsoft_unavailable_desc: 'Microsoft integration isn\u2019t configured for this workspace yet. Ping your admin.',
        coming_soon_badge: 'Coming soon',
        permission_missing_title: 'A permission is missing to finish connecting',
        permission_missing_desc: 'You authorized {{provider}} but this is missing: {{scopes}}. Without it we can\u2019t sync real data.',
        reauthorize_cta: 'Authorize again',
        footnote: 'We only access what\u2019s necessary and never send emails without your permission. You can disconnect anytime.',
      },
      start: {
        eyebrow: 'Your account is ready',
        eyebrow_with_name: 'Welcome, {{name}}',
        title: 'How do you want to start?',
        subtitle: 'Pick where we activate Quantro first. You can change this later.',
        recommended_badge: 'Recommended',
        continue_cta: 'Start',
        choice_email_title: 'Connect email',
        choice_email_desc: 'Fastest path. Quantro starts organizing your conversations from minute one.',
        choice_tools_title: 'Connect tools',
        choice_tools_desc: 'Calendar, CRM and webhooks. We build your operation piece by piece.',
        choice_explore_title: 'Explore first',
        choice_explore_desc: 'See Quantro in action with demo data. Connect whenever you want.',
      },
      checkout: {
        eyebrow: 'Activated',
        title: 'Quantro is ready to operate',
        subtitle: 'Your Starter plan is active. Let\u2019s connect your operation.',
        plan_starter_name: 'Starter plan',
        plan_active_badge: 'Active',
        feat_inbox: 'Smart Inbox with AI triage',
        feat_calendar: 'Calendar and availability sync',
        feat_crm: 'CRM with automated follow-ups',
        feat_automations: 'Automations and webhooks',
        continue_cta: 'Continue',
        upgrade_link: 'See higher tiers later',
      },
      inbox: {
        title: 'Connect your email',
        subtitle: 'This is where it all begins.\n\nQuantro organizes your conversations, detects opportunities\nand suggests actions automatically.',
        connect_cta: 'Connect with Google',
        skip_cta: 'Continue with demo',
        checking_connection: 'Checking your Google connection...',
        preview_microcopy: 'This is how your smart inbox will look once you connect your email.',
        preview_end_microcopy: 'Now connect your email to see it with your real data.',
        preview_header: 'Smart inbox',
        phase_analyzing: 'Analyzing conversations...',
        phase_detecting: 'Detecting opportunities...',
        phase_priorit: 'Prioritizing follow-ups...',
        preview_row1_subject: 'Quote for Q3 project',
        preview_row1_snippet: 'Hi, wanted to confirm the proposal before Friday...',
        preview_row2_subject: 'Postponed meeting',
        preview_row2_snippet: 'I wrote 4 days ago about rescheduling and haven\u2019t heard back...',
        preview_row3_subject: 'Pending signature',
        preview_row3_snippet: 'Attached updated contract. We need to close it today...',
        tag_opportunity: 'Opportunity',
        tag_no_reply: 'No reply',
        tag_for_today: 'For today',
      },
      calendar: {
        title: 'Sync your calendar',
        subtitle: 'To schedule, detect availability\nand automate follow-ups.',
        connect_cta: 'Connect Google Calendar',
        skip_cta: 'Continue with demo',
        already_connected_title: 'Calendar already synced',
        already_connected_desc: '{{account}} is already connected. Moving on to the next step.',
        preview_microcopy: 'This is how Quantro will coordinate your schedule when you connect your calendar.',
        preview_end_microcopy: 'Connect your calendar to automate availability and follow-ups.',
        preview_header: 'Today\u2019s schedule',
        phase_syncing: 'Syncing calendar...',
        phase_detecting: 'Detecting availability...',
        phase_aligning: 'Aligning follow-ups...',
        gap_label: 'Available · ideal for a call',
        event1_title: 'Meeting with Andrea Castro',
        event1_detail: 'Google Meet · existing client',
        event2_title: 'Product demo',
        event2_detail: 'Virtual room · prospect',
        event3_title: 'Sales team sync',
        event3_detail: 'Office · internal',
      },
      crm: {
        eyebrow_optional: 'Optional',
        title: 'Connect your CRM',
        subtitle: 'Sync contacts, deals\nand automate follow-ups.',
        connect_cta: 'Connect CRM',
        skip_cta: 'Continue with demo',
        preview_microcopy: 'This is how your pipeline will look once you connect your CRM.',
        preview_end_microcopy: 'Connect your CRM to work with your real contacts.',
        preview_header: 'Active pipeline',
        phase_matching: 'Syncing contacts...',
        phase_staging: 'Classifying by stage...',
        phase_flagging: 'Detecting inactive...',
        col_lead: 'Lead',
        col_qualified: 'Qualified',
        col_negotiation: 'Negotiation',
        col_closed: 'Closed',
        deal_lead_1: 'Anna M. · New website',
        deal_lead_2: 'Peter T. · Consulting',
        deal_qual_1: 'Martha R. · Pro plan',
        deal_qual_2: 'Lewis G. · Audit',
        deal_neg_1: 'Investments N. · Annual',
        deal_closed_1: 'Studio K. · Q3',
      },
      automations: {
        eyebrow_optional: 'Optional',
        title: 'Automate your operation',
        subtitle: 'Receive events from any system\nand let Quantro act automatically.',
        connect_cta: 'Set up webhooks',
        skip_cta: 'Continue with demo',
        preview_microcopy: 'This is how Quantro will react automatically to events from your operation.',
        preview_end_microcopy: 'Set up your webhooks to automate real actions.',
        preview_header: 'Automatic flow',
        live_indicator: 'Live',
        phase_listening: 'Listening for webhooks...',
        phase_classifying: 'Classifying events...',
        phase_reacting: 'Executing actions...',
        node_event_label: 'Webhook',
        node_event_title: 'Payment confirmed',
        node_event_detail: 'Stripe · checkout.session.completed',
        node_brain_label: 'Quantro',
        node_brain_title: 'Classified · New customer',
        node_brain_detail: 'High confidence · 0.94',
        node_action_label: 'Action',
        node_action_title: 'Create follow-up + schedule onboarding',
        node_action_detail: 'Notification sent to team',
      },
      ready: {
        activating: 'Activating Quantro...',
        eyebrow_real: 'Live',
        eyebrow_demo: 'Demo mode',
        title_real: 'Quantro is now operating with your real data',
        title_real_with_name: '{{name}}, Quantro is now operating with your real data',
        title_demo: 'Quantro is ready. Connect your real data whenever you want',
        title_demo_with_name: '{{name}}, Quantro is ready to operate',
        subtitle_real: 'Here\u2019s what we detected in your operation. Start from your inbox.',
        subtitle_demo: 'These are demo values so you can see the potential. Connect your accounts whenever you want to replace them with real data.',
        detected_label: 'Detected in your operation',
        metric_conversations: 'active conversations',
        metric_opportunities: 'follow-up opportunities',
        metric_contacts: 'available contacts',
        metric_events: 'upcoming meetings',
        enter_cta: 'Go to Smart Inbox',
        demo_hint: 'You\u2019re running on demo data. When you\u2019re ready, connect your integrations from Settings → Integrations.',
      },
    },
    invite: {
      page_title: 'You\u2019ve been invited to a workspace',
      page_subtitle: 'Review the details before accepting.',
      workspace_label: 'Workspace',
      role_label: 'Assigned role',
      expires_label: 'Expires',
      accept_cta: 'Accept invitation',
      decline_cta: 'Cancel',
      accepting: 'Accepting...',
      success_title: 'You joined the workspace',
      success_subtitle: 'Taking you to the dashboard.',
      already_member: 'You are already a member of this workspace.',
      error_invalid: 'This invite is invalid or has expired.',
      error_revoked: 'This invite has been revoked.',
      error_max_uses: 'This invite has reached its usage limit.',
      sign_in_required_title: 'Sign in to continue',
      sign_in_required_desc: 'You need a Quantro Flow account to accept this invite.',
    },
    common: {
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      confirm: 'Confirm',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      connect: 'Connect',
      disconnect: 'Disconnect',
      update: 'Update',
      details: 'Details',
      close: 'Close',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      search: 'Search',
      optional: 'optional',
      required: 'required',
      yes: 'Yes',
      no: 'No',
      retry: 'Retry',
      copy: 'Copy',
      copied: 'Copied',
    },

    language: {
      label: 'Language',
      spanish: 'Español',
      english: 'English',
      switched_to: 'Language switched to {{language}}',
    },

    simulation: {
      label: 'Simulation Mode',
      badge_simulation: 'SIMULATION',
      badge_live: 'LIVE',
      tooltip_on: 'You are viewing sample data. Turn off to use your real integrations.',
      tooltip_off: 'Using real data from your connected integrations.',
      banner_title: 'Simulation Mode',
      banner_description_on:
        'You are exploring Quantro Flow with a realistic sample dataset. Your real integrations are not being used yet.',
      banner_description_off:
        'Quantro Flow is operating with your real integrations and live data.',
      confirm_title: 'Switch to Live Mode',
      confirm_body:
        "You're switching to Live Mode. Your real integrations and workspace data will be used. Sample data will be hidden.",
      confirm_go_live: 'Go Live',
      confirm_stay: 'Stay in Simulation',
      toast_on: 'Simulation Mode activated',
      toast_off: 'Live Mode activated',
      toast_failed: 'Failed to switch mode',
      // Live mode empty states (shared copy across modules)
      live_empty_title: 'No real data yet',
      live_empty_subtitle: 'No real workspace data yet. Connect your integrations or create your first record manually.',
      live_empty_cta_connect: 'Connect integrations',
      live_empty_cta_simulation: 'Try Simulation Mode',
      live_empty_inbox: 'Your inbox is empty. Connect Gmail or another channel to start receiving real messages.',
      live_empty_crm: 'No real contacts yet. Connect your CRM or add one manually.',
      live_empty_schedule: 'Your calendar is empty. Connect Google Calendar or create your first event.',
      live_empty_activity: 'No real activity yet. Activity will appear here as you start operating.',
      live_empty_content: 'No generated content yet. Use the Content Engine to create your first piece.',
      live_empty_onboarding: 'No team members are onboarding yet. Add your first team member to get started.',
    },

    login: {
      title: 'Welcome to Quantro Flow',
      subtitle: 'Your autonomous operating system for business.',
      sign_in_google: 'Sign in with Google',
      loading: 'Verifying credentials...',
    },

    sidebar: {
      brand: 'Quantro Flow',
      brand_subtitle: 'BUSINESS OS',
      dashboard: 'Dashboard',
      smart_inbox: 'Smart Inbox',
      schedule: 'Schedule',
      crm: 'CRM',
      onboarding: 'Team Onboarding',
      content_engine: 'Content Engine',
      automation: 'Automation',
      members: 'Members',
      settings: 'Settings',
      integrations_label: 'INTEGRATIONS',
      system_running: 'System Running',
      synced: 'Synced {{time}}',
    },

    dashboard: {
      title: 'Dashboard',
      subtitle: 'Business operations · Real-time overview',
      live_mode: 'Live Data Mode',
      simulation_mode: 'Simulation Mode Active',
      kpi: {
        team: 'Team Members',
        team_active: '{{count}} active',
        schedule: 'Upcoming Meetings',
        schedule_window: 'next 7 days',
        inbox: 'Inbox Requests',
        inbox_processed: '{{count}} processed',
        crm: 'CRM Contacts Synced',
        crm_new: '{{count}} new this week',
      },
      live_activity: 'Live Activity',
      real_time: 'Real-time',
      ai_suggestions: 'AI Suggestions',
      quick_actions: 'Quick Actions',
      today_meetings: "Today's Meetings",
      no_meetings: 'No meetings scheduled today',
      view_full_calendar: 'View Full Calendar',
      ai_engine: 'AI Engine',
      running: 'Running',
      active: 'Active',
      no_recent_activity: 'No recent activity',
      activity_hint: 'Activity will appear here as your system processes items',
      no_suggestions: 'No suggestions yet',
      integration_connected: '{{count}} Integration Connected',
      integrations_connected: '{{count}} Integrations Connected',
      integration_sync_status: 'Syncing in real-time',
      manage: 'Manage',
    },

    system_health: {
      title: 'System Health',
      tagline: 'Quantro Flow detects and fixes issues before you notice them.',
      status_healthy: 'System Status: Healthy',
      status_repaired: 'System Status: Auto-Repaired',
      status_degraded: 'System Status: Degraded',
      all_operational: 'All systems operational',
      last_check: 'Last check: {{time}}',
      subcopy_healthy:
        'Quantro Flow is actively maintaining your integrations. Any inconsistencies are detected and resolved automatically.',
      subcopy_repaired:
        'Quantro Flow detected missing components and repaired them automatically.',
      subcopy_degraded:
        'Some components require attention. Quantro Flow is working to resolve them.',
      checks: {
        integrations_stable: 'Integrations stable',
        integrations_stable_ok: '{{have}}/{{total}} providers registered',
        integrations_stable_fail: 'Missing providers ({{missing}})',
        data_consistency: 'Data consistency verified',
        data_consistency_ok: 'Business profile present',
        data_consistency_fail: 'Business profile missing',
        no_issues: 'No issues detected',
        no_issues_ok: 'All systems operational',
        no_issues_repairs: '{{count}} auto-repair(s) on last startup',
        issues_detected: 'Issues detected',
      },
      auto_resolved_header: 'Auto-resolved on last startup',
      repair_provider_restored:
        "Missing integration '{{name}}' was restored automatically.",
      repair_metadata_backfilled:
        "Metadata repaired for '{{name}}' ({{fields}}).",
      repair_toast_single: 'System repaired missing integrations automatically',
      repair_toast_multi: 'System repaired {{count}} missing integrations automatically',
    },

    settings: {
      title: 'Settings',
      subtitle: 'Configure your Business OS',
      tabs: {
        integrations: 'Integrations',
        automation: 'Automation',
        business_profile: 'Business Profile',
        workspace: 'Workspace',
      },
      automation: {
        heading: 'Automation Settings',
        description:
          'Configure your automation policies, confidence thresholds, and escalation rules.',
        manage_button: 'Manage Automation Policies',
        overview_title: 'Quick Overview',
        overview_auto: 'Auto-run enabled for high-confidence items',
        overview_manual: 'Manual approval required for medium confidence',
        overview_escalation: 'Escalation triggered for low confidence or conflicts',
      },
      profile: {
        heading: 'Business Profile',
        description: 'Configure your business type and customize system terminology',
        industry_label: 'Industry',
        industry_helper:
          'The system will adapt its language and AI suggestions based on your industry',
        use_case_label: 'Use Case (Optional)',
        use_case_placeholder: 'Describe how you use this Business OS...',
        naming_heading: 'Custom Entity Naming',
        naming_helper:
          'Customize terminology throughout the system. Leave blank to use industry defaults.',
        label_contacts: 'Contacts Label',
        label_team: 'Team Members Label',
        label_meetings: 'Meetings Label',
        label_events: 'Events Label',
        label_services: 'Services/Products Label',
        simulation_heading: 'Simulation Mode',
        simulation_description:
          'Load realistic sample operational data for your selected industry. Perfect for testing workflows, validating automations, and demoing the system before connecting live integrations.',
        simulation_active: 'Active: The system is populated with simulated data',
        save_button: 'Save Business Profile',
        saving: 'Saving...',
        saved_toast: 'Business Profile updated successfully',
        save_failed_toast: 'Failed to update Business Profile',
      },
      workspace: {
        heading: 'Workspace',
        description: 'Workspace settings and team management',
        name_label: 'Workspace Name',
        name_placeholder: 'My Business',
        phase_note:
          'Authentication and multi-tenant workspace features will be available in Phase 6.',
      },
    },

    integrations: {
      groups: {
        ai: 'AI & Intelligence',
        email: 'Email & Calendar',
        crm: 'CRM',
        automation: 'Webhooks & Endpoints',
      },
      status: {
        connected: 'Connected',
        not_connected: 'Not Connected',
        last_sync: 'Last sync: {{time}}',
      },
      actions: {
        save_and_connect: 'Save & Connect',
        connect_google: 'Connect with Google',
        enable_webhooks: 'Enable Webhooks',
        test_connection: 'Test Connection',
        update_config: 'Update Config',
        disconnect: 'Disconnect',
      },
      openai: {
        name: 'OpenAI / LLM Provider',
        description:
          'Powers AI classification, drafting, and auto-execution across your workflows.',
        helper:
          'This workspace is currently powered by the Emergent Universal Key. Add your own key to override it per workspace.',
        api_key: 'API Key',
        default_model: 'Default Model',
      },
      gmail: {
        name: 'Gmail',
        description: 'Sync incoming email into Smart Inbox and let the system draft replies.',
        account: 'Connected Account',
      },
      calendar: {
        name: 'Google Calendar',
        description: 'Two-way sync events, bookings, and availability windows.',
        calendar_id: 'Calendar ID',
      },
      crm: {
        name: 'CRM System',
        description:
          'Bi-directional sync with HubSpot, GoHighLevel, Pipedrive, or any CRM with an API.',
        provider: 'CRM Provider',
        api_key: 'API Key',
        base_url: 'Base URL (optional)',
      },
      webhook: {
        name: 'Inbound Webhooks',
        description:
          'Forward events from any service into Business OS. Use the endpoint below in your external tools.',
        endpoint_label: 'YOUR INBOUND ENDPOINT',
        shared_secret: 'Shared Secret (optional)',
        secret_placeholder: 'Generate a random string',
        coming_soon_badge: 'Coming Soon',
        coming_soon_description: 'Inbound webhooks are not available yet. We are working on this feature.',
      },
      real_connect: {
        title: 'Connect Gmail / Calendar',
        description: 'Real Gmail and Google Calendar connection happens via OAuth, not a form. Use the welcome flow to authorize your Google account.',
        cta: 'Connect real inbox',
      },
      toasts: {
        connected: '{{provider}} connected successfully',
        disconnected: '{{provider}} disconnected',
        test_ok: '{{provider}} connection is healthy',
        test_fail: '{{provider}} is not connected',
        load_failed: 'Failed to load integrations',
        connect_failed: 'Failed to connect {{provider}}',
        endpoint_copied: 'Endpoint copied to clipboard',
        copy_failed: 'Copy unavailable in this browser — please select and copy manually',
      },
    },

    data_mode_banner: {
      demo_title_inbox: 'Demo mode · Inbox',
      demo_title_schedule: 'Demo mode · Schedule',
      demo_desc_inbox: 'You\u2019re viewing sample emails. Connect your inbox to work with your real data.',
      demo_desc_schedule: 'You\u2019re viewing sample events. Connect your calendar to coordinate your real agenda.',
      demo_cta_inbox: 'Connect inbox',
      demo_cta_schedule: 'Connect calendar',
      real_title: 'Synced with {{provider}}',
      last_sync_label: 'Last sync {{when}}',
      last_sync_never: 'not synced yet',
      last_sync_error_label: 'Last sync failed — we\u2019ll retry automatically',
      paused_badge: 'Paused',
      sync_now: 'Sync',
      syncing: 'Syncing\u2026',
      sync_success_title: 'Sync complete',
      sync_success_desc: '{{emails}} emails and {{events}} events from {{provider}}.',
      sync_failed_title: 'Couldn\u2019t sync',
      sync_failed_desc: 'Try again in a few seconds.',
    },

    sync_preference: {
      title: 'How do you want to sync your inbox?',
      description: 'We already imported and classified your first batch of emails. Choose how to keep it up to date from now on.',
      auto_title: 'Automatic (every 15 min)',
      auto_desc: 'New emails get imported and classified on their own, no action needed.',
      manual_title: 'Manual (I decide when)',
      manual_desc: 'Only syncs when you press "Sync". Useful to control AI credit usage.',
      footer_hint: 'You can change this later in Settings.',
      toast_auto: 'Automatic sync enabled',
      toast_manual: 'Manual sync enabled',
      toast_error: 'Couldn\u2019t save your preference, please try again',
    },

    smart_inbox: {
      title: 'Smart Inbox',
      subtitle: 'AI classification and automated triage',
      empty_state: 'No inbox items',
      empty_hint: 'Your incoming requests will appear here',
      empty_filter_title: 'Nothing here yet',
      empty_filter_processed: 'You haven\u2019t classified any messages yet. Go to the "New" tab and use "Classify" to analyze them.',
      empty_filter_actioned: 'No completed messages yet.',
      empty_filter_declined: 'You haven\u2019t skipped any messages yet.',
      empty_filter_new: 'You\u2019re all caught up! No new messages to classify.',
      empty_filter_cta: 'View all messages',
      analyze: 'Analyze',
      analyze_all: 'Analyze All',
      approve: 'Approve',
      escalate: 'Escalate',
      filters: {
        all: 'All',
        unread: 'Unread',
        needs_review: 'Needs review',
        auto_actioned: 'Auto-actioned',
        escalated: 'Escalated',
        processed: 'Processed',
      },
      intent: {
        booking: 'Booking',
        onboarding: 'Onboarding',
        follow_up: 'Follow-up',
        inquiry: 'Inquiry',
        escalation: 'Escalation',
        spam: 'Spam',
        needs_review: 'Needs review',
      },
      status: {
        new: 'New',
        analyzed: 'Analyzed',
        needs_review: 'Needs review',
        auto_actioned: 'Auto-actioned',
        escalated: 'Escalated',
        processed: 'Processed',
      },
      confidence_label: 'Confidence',
      suggested_action: 'Suggested action',
      execution_trail: 'Execution Trail',
      analyzing: 'Analyzing...',
      batch_selected: '{{count}} selected',
      batch_analyze: 'Analyze selected',
      clear_selection: 'Clear selection',
      process_inbox: 'Process Inbox',
      view_details: 'View details',
      mark_processed: 'Mark processed',
      escalation_reason: 'Escalation reason',
      toasts: {
        analyzed: 'Item analyzed successfully',
        analyze_failed: 'Failed to analyze',
        batch_done: '{{count}} items analyzed',
        batch_failed: 'Batch analysis failed',
        marked_processed: 'Item marked as processed',
        mark_failed: 'Failed to mark item',
        escalated: 'Item escalated',
      },
    },

    crm: {
      title: 'CRM',
      subtitle: 'Contacts and relationships',
      empty_state: 'No contacts yet',
      select_prompt: 'Select a contact to view their details',
      empty_hint: 'Your synced contacts will appear here',
      search_placeholder: 'Search contacts...',
      add_contact: 'Add Contact',
      new_contact: 'New Contact',
      table: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        status: 'Status',
        source: 'Source',
        created: 'Created',
        last_contact: 'Last contact',
        actions: 'Actions',
      },
      status: {
        lead: 'Lead',
        contacted: 'Contacted',
        qualified: 'Qualified',
        customer: 'Customer',
        churned: 'Churned',
        active: 'Active',
      },
      details: {
        title: 'Contact Details',
        edit: 'Edit',
        delete: 'Delete',
        activities: 'Activities',
        notes: 'Notes',
      },
      form: {
        name_label: 'Full name',
        email_label: 'Email',
        phone_label: 'Phone',
        status_label: 'Status',
        source_label: 'Source',
        notes_label: 'Notes',
      },
      toasts: {
        created: 'Contact created',
        updated: 'Contact updated',
        deleted: 'Contact deleted',
        save_failed: 'Failed to save contact',
      },
    },

    schedule: {
      title: 'Schedule',
      subtitle: 'Meetings and events',
      empty_state: 'No events scheduled',
      empty_hint: 'Your upcoming meetings will appear here',
      add_event: 'Add Event',
      today: 'Today',
      tomorrow: 'Tomorrow',
      this_week: 'This week',
      upcoming: 'Upcoming',
      past: 'Past',
      duration: '{{minutes}} min',
      attendees: '{{count}} attendees',
      with: 'with {{person}}',
      status: {
        scheduled: 'Scheduled',
        completed: 'Completed',
        cancelled: 'Cancelled',
        no_show: 'No show',
      },
      form: {
        title_label: 'Title',
        date_label: 'Date',
        time_label: 'Time',
        duration_label: 'Duration (minutes)',
        location_label: 'Location',
        attendees_label: 'Attendees',
        notes_label: 'Notes',
      },
      toasts: {
        created: 'Event created',
        updated: 'Event updated',
        cancelled: 'Event cancelled',
        save_failed: 'Failed to save event',
      },
    },

    content_engine: {
      title: 'Content Engine',
      subtitle: 'AI-powered content generation',
      tabs: {
        generate: 'Generate',
        templates: 'Templates',
        history: 'History',
      },
      generate: {
        topic_label: 'Topic',
        topic_placeholder: 'E.g. new product launch...',
        tone_label: 'Tone',
        type_label: 'Content type',
        generate_button: 'Generate Content',
        regenerate: 'Regenerate',
        copy_button: 'Copy',
        generating: 'Generating content...',
        social_post: 'Social Post',
        email_draft: 'Email Draft',
        hashtags: 'Hashtags',
        subject: 'Subject',
        body: 'Body',
        call_to_action: 'Call to Action',
      },
      tone: {
        professional: 'Professional',
        friendly: 'Friendly',
        warm: 'Warm',
        urgent: 'Urgent',
        playful: 'Playful',
      },
      templates: {
        heading: 'Templates',
        new_template: 'New Template',
        edit: 'Edit',
        delete: 'Delete',
        use: 'Use',
        empty: 'No templates yet',
        variables: 'Variables',
        category_label: 'Category',
        type_label: 'Type',
        name_label: 'Name',
        content_label: 'Content',
        save: 'Save Template',
      },
      history: {
        heading: 'Generation History',
        empty: 'No generations yet',
        empty_hint: 'Your generated content will appear here',
      },
      toasts: {
        generated: 'Content generated',
        generate_failed: 'Failed to generate content',
        copied: 'Copied to clipboard',
        copy_failed: 'Failed to copy',
        template_saved: 'Template saved',
        template_deleted: 'Template deleted',
        save_failed: 'Failed to save',
      },
    },

    onboarding: {
      // Onboarding page (existing — customer onboarding flows)
      title: 'Onboarding',
      subtitle: 'Client onboarding flows',
      empty_state: 'No active onboarding flows',
      empty_hint: 'Onboarding flows you create will appear here',
      new_flow: 'New Flow',
      progress: 'Progress',
      steps: 'Steps',
      step_completed: 'Completed',
      step_pending: 'Pending',
      step_in_progress: 'In progress',
      start: 'Start',
      complete: 'Complete',
      resume: 'Resume',
      cancel: 'Cancel',
      form: {
        client_label: 'Client',
        type_label: 'Onboarding type',
        start_date_label: 'Start date',
      },
      toasts: {
        created: 'Onboarding flow created',
        updated: 'Flow updated',
        completed: 'Flow completed',
        step_completed: 'Step completed',
        save_failed: 'Failed to save',
      },

      // Phase 7b-ext — Members → Onboarding mini-tab
      tab_label: 'Onboarding',
      summary: 'Visual progress tracking for each member\u2019s onboarding.',
      member_progress: 'Member progress',
      status_pending: 'Pending',
      status_in_progress: 'In progress',
      status_completed: 'Completed',
      status_blocked: 'Blocked',
      step_invitation_sent: 'Invitation sent',
      step_invitation_sent_done: 'This member has already received the invitation.',
      step_invitation_sent_pending: 'Generate a link to invite them.',
      step_account_created: 'Account created',
      step_account_created_done: 'The account was created successfully.',
      step_account_created_pending: 'The account hasn\u2019t been created yet.',
      step_companies_assigned: 'Companies assigned',
      step_companies_assigned_done: 'The member already has assigned companies.',
      step_companies_assigned_pending: 'Assign a company before activating access.',
      step_role_configured: 'Role configured',
      step_role_configured_done: 'The member role has been configured.',
      step_role_configured_pending: 'Still a viewer until you assign a role.',
      step_first_login: 'First login',
      step_first_login_done: 'The member has logged in for the first time.',
      step_first_login_pending: 'Hasn\u2019t signed in yet.',
      action_copy_invite: 'Copy invite link',
      action_resend_invite: 'Resend invite',
      action_mark_complete: 'Mark onboarding complete',
      action_revoke_access: 'Revoke access',
      empty: 'No members to display.',
      mark_complete_confirm: 'Mark this member\u2019s onboarding as complete?',
    },

    automation: {
      title: 'Automation Policies',
      subtitle: 'Auto-execution and escalation rules',
      tabs: {
        policies: 'Policies',
        escalations: 'Escalations',
      },
      policies: {
        heading: 'Confidence Policies',
        description: 'Define what happens based on the confidence level of AI analysis.',
        high_confidence: 'High Confidence',
        medium_confidence: 'Medium Confidence',
        low_confidence: 'Low Confidence',
        threshold_label: 'Threshold',
        action_label: 'Action',
        enabled_label: 'Enabled',
        new_policy: 'New Policy',
        save: 'Save Policy',
        action_auto: 'Auto-execute',
        action_manual: 'Manual approval',
        action_review: 'Needs review',
        action_escalate: 'Escalate',
      },
      escalations: {
        heading: 'Escalation Rules',
        description: 'Advanced conditions that force review or team notification.',
        new_rule: 'New Rule',
        rule_type: 'Rule Type',
        condition: 'Condition',
        priority: 'Priority',
        notify_team: 'Notify team',
        types: {
          intent: 'Intent',
          keyword: 'Keyword',
          calendar_conflict: 'Calendar conflict',
          incomplete_entities: 'Incomplete entities',
          urgency: 'Urgency',
          contact_type: 'Contact type',
        },
        priority_levels: {
          low: 'Low',
          medium: 'Medium',
          high: 'High',
          critical: 'Critical',
        },
      },
      toasts: {
        policy_saved: 'Policy saved',
        policy_deleted: 'Policy deleted',
        rule_saved: 'Rule saved',
        rule_deleted: 'Rule deleted',
        save_failed: 'Failed to save',
      },
    },

    // ────────────────────────────────────────────────────
    // Decisions — key pattern for the decision system
    // ────────────────────────────────────────────────────
    decisions: {
      revenue: {
        raise_prices: {
          title: 'Raise prices by {{percent}}%',
          summary:
            'Current demand and margins support a {{percent}}% pricing adjustment.',
          impact: 'Estimated revenue lift of {{lift}}%.',
          action_label: 'Apply price change',
        },
        launch_campaign: {
          title: 'Launch retention campaign',
          summary:
            '{{count}} customers show risk signals — a targeted campaign can win them back.',
          action_label: 'Launch campaign',
        },
      },
      action_center: {
        no_pending: 'No pending decisions',
        pending_count: '{{count}} pending decision',
        pending_count_plural: '{{count}} pending decisions',
      },
    },

    // ────────────────────────────────────────────────────
    // Agents — labels and descriptions
    // ────────────────────────────────────────────────────
    agents: {
      pricing: {
        label: 'Pricing Agent',
        description: 'Monitors margins and recommends price adjustments.',
      },
      retention: {
        label: 'Retention Agent',
        description: 'Detects at-risk customers and proposes campaigns.',
      },
      triage: {
        label: 'Triage Agent',
        description: 'Classifies and routes incoming inbox items.',
      },
    },
  },
};

export const SUPPORTED_LANGUAGES = [
  { code: 'es', label: 'Español', englishLabel: 'Spanish' },
  { code: 'en', label: 'English', englishLabel: 'English' },
];

export const DEFAULT_LANGUAGE = 'es';
export const FALLBACK_LANGUAGE = 'en';
